import React, { createRef, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
// @todo replace with our error boundary
import { withChalkTalkErrorBoundary } from '@components/ChalkTalkErrorBoundary/ChalkTalkErrorBoundary';
import { useCheckSessionItemsTaken } from '@components/Component/LearnosityExams/mutations';
import LoadingIndicator from '@components/LoadingIndicator';
import Dialog from '@components/Atoms/Dialog';
import Typography from '@components/Atoms/Typography';
import useThrowError from '../../Hooks/useThrowError';
import { AssessmentPlaceholder } from './Assessment.style';

const TestHarness = lazy(() =>
  import('../TestHarness').then((module) => ({
    default: module.memoTestHarness,
  })),
);
const outOfFullScreenDialog = {
  header: 'You are currently out of full screen mode',
  body: 'Resuming the exam without full screen may be considered as cheating and the proctor may take action',
  buttons: [
    {
      button_id: 'resume_without_full_screen',
      label: 'Resume Without Full Screen',
      is_primary: false,
    },
    {
      button_id: 'enter_full_screen',
      label: 'Enter Full Screen',
      is_primary: true,
    },
  ],
};

interface TestAssessmentProps {
  title: string;
  subtitle: string;
  sessionId: string;
  userId: string;
  activityId: string;
  maxTime: number;
  activityTemplateId: string;
  isBranching: boolean;
  testletSessionId: number;
  handleActivityExit?: () => void;
  handleActivitySubmit?: () => void;
  handleActivityStart?: () => void;
  handleFocus?: (widget: any) => void;
  handleUnfocus?: (widget: any) => void;
}

const defaultLoadingMessage = 'Loading Your Test...';
const attemptedMaxMessage =
  'You have reached the maximum number of questions for this assessment. Attempting to submit and score the assessment...';

const TestAssessment = ({
  userId,
  sessionId,
  activityId,
  title,
  subtitle,
  maxTime,
  activityTemplateId,
  isBranching,
  testletSessionId,
  handleActivitySubmit,
  handleActivityExit,
  handleActivityStart,
  handleFocus,
  handleUnfocus,
}: TestAssessmentProps) => {
  const history = useHistory();
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const widgetRef = createRef<any>();
  const throwError = useThrowError();
  const [isReady, setIsReady] = useState(false);
  // get the latest items taken for a session vs the total number of questions
  // for itembranching type assessments, theres a bug where resuming a fully taken test causes the player to crash
  const [testState, setTestState] = useState<'initial' | 'resume'>('initial');
  const { mutate: fetchSessionItemsTaken } = useCheckSessionItemsTaken(sessionId);
  const [answeredAllQuestions, setAnsweredAllQuestions] = useState<boolean | null>(null);
  const [showLoading, setShowLoading] = useState<boolean>(true);

  // Navigation blocking state and refs
  const [showNavigationWarning, setShowNavigationWarning] = useState(false);
  const isNavigatingAwayRef = useRef(false);
  const pendingLocationRef = useRef<any>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const handleSuccess = useCallback(() => {
    handleActivitySubmit?.();
    setSubmissionComplete(true);
  }, [handleActivitySubmit]);

  const renderAssessment = !submissionComplete;

  const onExitFullScreen = useCallback(() => {
    const widget = widgetRef.current?.getWidgetInstance();
    if (widget && !answeredAllQuestions) {
      widget.dialogs().custom.show(outOfFullScreenDialog);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, answeredAllQuestions]);

  const handleEnterFullScreen = () => {
    const container = document.querySelector(
      `#assessmentWidget-${sessionId}`,
    ) as HTMLDivElement;
    if (container) {
      const goBackButton = document.getElementById('LE_Assessment_GoBackButton');
      if (goBackButton) {
        goBackButton.focus();
      }
      container.requestFullscreen();
    }
  };

  const config = React.useMemo(
    () => ({
      // https://help.learnosity.com/hc/en-us/articles/16560466913181-config-time-Initialization-Items-API
      time: {
        max_time: maxTime,
        limit_type: 'soft' as const,
        countdown_option: false,
      },
      navigation: {
        auto_save: {
          changed_responses_only: false,
          save_interval_duration: 10, // 10 seconds
        },
        warning_on_change: isBranching,
        skip_submit_confirmation: !isBranching,
        // for branching testlets, we don't want to load resource_items
        // if student has taken the max number of items
        ...(answeredAllQuestions ? { resource_items: [] } : {}),
      },
      configuration: {
        lazyload: true,
        onsave_redirect_url: false,
        onsubmit_redirect_url: false,
      },
      // Custom labels for the warning on change dialog
      // https://help.learnosity.com/hc/en-us/articles/360000758697-Internationalizing-and-Localizing-the-Assessment-Experience
      labelBundle: {
        warningOnChangeHeadingMsg:
          'You have not selected an answer, you will not be able to return to this question',
        warningOnChangeBodyMsg:
          'Are you ready to continue to the next question? If you want to pause, cancel and leave the page after selecting your answer.',
      },
      questions_api_init_options: {
        attribute_overrides: {
          // don't show Check Answer button
          instant_feedback: false,
        },
      },
      regions: 'main',
      region_overrides: {
        items: [
          {
            type: 'slider_element',
            vertical_stretch_option: true,
            scrollable_options: false,
          },
        ],
        'top-right': [
          {
            type: 'itemcount_element',
          },
        ],
        bottom: [
          {
            type: 'next_button',
          },
          {
            type: 'horizontaltoc_element',
          },
          {
            type: 'previous_button',
            show_label_option: true,
          },
        ],
        right: [
          {
            type: 'fullscreen_button',
          },
          {
            type: 'accessibility_button',
          },
          {
            type: 'resource_button',
          },
          {
            type: 'notepad_button',
          },
        ],
        'bottom-right': false,
      },
      subtitle,
      title,
    }),
    [maxTime, answeredAllQuestions],
  );

  useEffect(() => {
    if (!isBranching) {
      setAnsweredAllQuestions(false);
      return;
    }

    fetchSessionItemsTaken(testletSessionId, {
      onSuccess: ({ response }) => {
        const attempted = response?.data?.num_attempted ?? 0;
        const max = response?.data?.max_questions ?? 9999;
        setAnsweredAllQuestions(attempted >= max);
        if (attempted > 0) {
          setTestState('resume');
        }
      },
      onError: () => setAnsweredAllQuestions(false),
    });
  }, [isBranching, fetchSessionItemsTaken, testletSessionId]);

  const errorCallback = React.useCallback(
    (error) => {
      throwError(error);
    },
    [throwError],
  );

  const handleNavigationConfirm = useCallback(() => {
    setShowNavigationWarning(false);
    isNavigatingAwayRef.current = false;
    if (unblockRef.current && pendingLocationRef.current) {
      unblockRef.current();
      history.push(
        pendingLocationRef.current.pathname + pendingLocationRef.current.search,
      );
      pendingLocationRef.current = null;
    }
  }, [history]);

  const handleNavigationCancel = useCallback(() => {
    setShowNavigationWarning(false);
    isNavigatingAwayRef.current = false;
  }, []);

  // React Router navigation blocker
  useEffect(() => {
    if (!isBranching || submissionComplete) {
      return;
    }

    unblockRef.current = history.block((location) => {
      if (!isNavigatingAwayRef.current) {
        isNavigatingAwayRef.current = true;
        pendingLocationRef.current = location;
        setShowNavigationWarning(true);
        return false; // Block navigation
      }
      return true; // Allow navigation
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [isBranching, submissionComplete, history]);

  // Browser navigation blocker (back/forward/close tab)
  useEffect(() => {
    if (!isBranching || submissionComplete) {
      return;
    }

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers show a generic message and ignore custom text
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isBranching, submissionComplete]);

  useEffect(() => {
    if (isReady) {
      const container = document.querySelector(
        `#assessmentWidget-${sessionId}`,
      ) as HTMLDivElement;
      setTimeout(() => {
        onExitFullScreen();
      }, 300);
      container?.addEventListener('fullscreenchange', (e: any) => {
        if (!e.target.fullscreenElement) {
          onExitFullScreen();
        }
      });
    }
  }, [isReady, onExitFullScreen, sessionId]);

  const eventHandlers = React.useMemo(
    () => [
      {
        callback: () => {
          setShowLoading(false);
          setIsReady(true);
        },
        name: 'test:ready',
      },
      {
        callback: (widget) => {
          widget.items().previous();
        },
        name: 'button:chalktalk_back:clicked',
      },
      {
        callback: (widget) => {
          // save the response and also the time remaining
          widget.save();
        },
        name: 'item:changing',
      },
      {
        callback: () => {
          handleActivityExit?.();
        },
        name: 'test:finished:save',
      },
      {
        callback: () => {
          // call /start endpoint to create local testlet session
          handleActivityStart?.();
        },
        name: 'test:start',
      },
      {
        callback: () => {
          handleSuccess();
        },
        name: 'test:submit:success',
      },
      {
        callback: (widget) => {
          handleFocus?.(widget);
        },
        name: 'focused',
      },
      {
        callback: (widget) => {
          handleUnfocus?.(widget);
        },
        name: 'unfocused',
      },
      {
        callback: (widget) => {
          widget.dialogs().custom.hide();
        },
        name: 'button:resume_without_full_screen:clicked',
      },
      {
        callback: (widget) => {
          widget.dialogs().custom.hide();
          handleEnterFullScreen();
        },
        name: 'button:enter_full_screen:clicked',
      },
    ],
    [sessionId],
  );

  if (submissionComplete) {
    return (
      <AssessmentPlaceholder>
        <span>Test Complete!</span>
      </AssessmentPlaceholder>
    );
  }

  if (!renderAssessment) {
    return null;
  }

  if (answeredAllQuestions === null) {
    return <LoadingIndicator content={defaultLoadingMessage} height100 noBackground />;
  }

  return (
    <>
      {showLoading && (
        <LoadingIndicator
          content={answeredAllQuestions ? attemptedMaxMessage : defaultLoadingMessage}
          height100
          noBackground
        />
      )}
      <div style={{ height: '100%' }}>
        <TestHarness
          ref={widgetRef}
          userId={userId}
          sessionId={sessionId}
          activityId={activityId}
          renderingType="assess"
          type="submit_practice"
          name={title}
          state={testState}
          activityTemplateId={activityTemplateId}
          config={config}
          errorCallback={errorCallback}
          eventHandlers={eventHandlers}
        />
      </div>
      <Dialog
        open={showNavigationWarning}
        onClose={handleNavigationCancel}
        title="Warning: Leaving Assessment"
        actions={[
          {
            label: 'Stay on this page',
            onClick: handleNavigationCancel,
            buttonProps: { variant: 'contained', color: 'primary' },
          },
          {
            label: 'Leave anyway',
            onClick: handleNavigationConfirm,
            buttonProps: { variant: 'text' },
          },
        ]}
      >
        <Typography variant="body1">
          Leaving this page will mark the question unattempted if unanswered, and you
          can&apos;t return to it. Make sure to answer before leaving.
        </Typography>
      </Dialog>
    </>
  );
};

// replace with ChalkTalkErrorBoundary
const wrappedTestAssessment = withChalkTalkErrorBoundary(
  TestAssessment as React.ComponentType,
);
export { wrappedTestAssessment as TestAssessment, TestAssessmentProps };
